import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import type { Dispatch } from 'redux';
import { connect, useDispatch } from 'react-redux';
import { useSessionStorage } from 'react-use';

import { IUserInfo } from 'APIPackage/AuthModule/interfaces/IUserInfo';
import { clearUserAction } from 'AuthPackage/MainModule/actions/authActions';
import { getAuthIsLoggedOut, getUserInfo, getUserRoleIsAdmin } from 'AuthPackage/MainModule/selectors/authSelectors';
import { IS_ESIA } from 'CorePackage/MainModule/constants/localStorageNames';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { KnmHeaderButtons } from 'KnmPackage/KnmHeaderModule/components/KnmHeaderButtons';
import { SwitchButton } from 'CorePackage/HeaderModule/components/SwitchButton';
import { getKnmFormRef } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { ROUTE_AUTH, ROUTE_ADMIN_USERS, ROUTE_INFO } from 'CorePackage/MainModule/routes/routes';
import { imageHeaderLogo } from 'UIPackage/IconsModule/components/imageHeaderLogo';
import { DecisionHeaderButtons } from 'DecisionPackage/DecisionHeaderModule/components/DecisionHeaderButtons';
import { getDecisionFormRef } from 'DecisionPackage/MainModule/selectors/decisionSelectors';

import { KnmInfo } from './KnmInfo';
import { NavBar } from './NavBar';
import styles from './Header.module.scss';
import { HeaderDropdown } from './HeaderDropdown';
import { DecisionInfo } from './DecisionInfo';
import { NotificationIcon } from './NotificationIcon/NotificationIcon';

interface IHeaderProps {
  showMenu?: boolean;
  style?: Record<string, string>;
}

interface IHeaderStoreProps {
  isAdmin: boolean | undefined;
  isKnm: boolean;
  isDecision: boolean;
  userInfo?: IUserInfo;
  isLoggedOut: boolean;
}

const HeaderWithoutStore = ({
  showMenu = true,
  isKnm,
  isDecision,
  userInfo,
  style,
  isAdmin,
  isLoggedOut,
}: IHeaderProps & IHeaderStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [isEsia] = useSessionStorage<boolean>(IS_ESIA);
  const history = useHistory();
  const id = useMemo(() => userInfo?.userProfile.id, [userInfo]);

  const userButton = useMemo(() => {
    if (history.location.pathname !== ROUTE_AUTH) {
      const linkAttributes = {
        ...((isKnm || isDecision) && { 'data-hidden': true }),
      };

      return !id ? (
        <Link to={ROUTE_AUTH} className={styles.SignInLink} {...linkAttributes}>
          Войти
        </Link>
      ) : (
        <>
          <SwitchButton />
          <NotificationIcon />
          <HeaderDropdown />
        </>
      );
    }

    return null;
  }, [history.location.pathname, id, isKnm, isDecision]);

  useEffect(() => {
    // FIXME clearUser должен вызываться только после того, как очистился флаг IS_ESIA
    if (isLoggedOut && !isEsia) {
      dispatch(clearUserAction());
    }
  }, [isLoggedOut, dispatch, isEsia]);

  const [isKnmSignButtonHover, setIsKnmSignButtonHover] = useState(false);

  const handleMouseOverKnmSignButton = useCallback(() => {
    setIsKnmSignButtonHover(true);
  }, [setIsKnmSignButtonHover]);

  const handleMouseOutKnmSignButton = useCallback(() => {
    setIsKnmSignButtonHover(false);
  }, [setIsKnmSignButtonHover]);

  return (
    <header className={styles.Header} style={style} data-scroll="false">
      <div className={styles.TopHeader}>
        <div className={styles.TopHeaderPrimaryContent}>
          <Link className={styles.LogoBlock} to={isAdmin ? ROUTE_ADMIN_USERS : ROUTE_INFO}>
            <img className={styles.LogoImg} alt="Генеральная прокуратура Российской Федерации" src={imageHeaderLogo} />
            <span className={styles.HeaderTitles}>
              <h3 className={styles.MainTitle}>Генеральная прокуратура РФ</h3>
              <h5 className={styles.SubTitle}>ФГИС «Единый реестр контрольных (надзорных) мероприятий»</h5>
              <h5 className={styles.SubTitle}>ФГИС «Единый реестр проверок»</h5>
            </span>
          </Link>
          {isKnm && <KnmInfo isKnmSignButtonHover={isKnmSignButtonHover} />}
          {isDecision && <DecisionInfo isHover={isKnmSignButtonHover} />}
        </div>
        {isKnm && (
          <KnmHeaderButtons
            className={styles.KnmButtons}
            location="header"
            onMouseOverKnmSignButton={handleMouseOverKnmSignButton}
            onMouseOutKnmSignButton={handleMouseOutKnmSignButton}
          />
        )}
        {isDecision && <DecisionHeaderButtons className={styles.KnmButtons} location="header" />}
        {userButton}
      </div>
      {showMenu && <NavBar />}
    </header>
  );
};

const mapStateToProps = (state: IRootState): IHeaderStoreProps => ({
  isKnm: !!getKnmFormRef(state),
  isDecision: !!getDecisionFormRef(state),
  userInfo: getUserInfo(state),
  isAdmin: getUserRoleIsAdmin(state),
  isLoggedOut: getAuthIsLoggedOut(state),
});

export const Header = connect(mapStateToProps)(HeaderWithoutStore);
